import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { CompletesDistributor } from '../../../create-surveys/shared/ui/multicountry/domain/completes-distributor';
import { notifyMessage } from '../../constants/notify-message';
import {
  McSpvEachSurveyRequest,
  MultiCountrySpvSurvey,
} from '../../../create-surveys/shared/ui/multicountry/interface/multi-country.interface';
import { deepCopy } from '../../../create-surveys/utils/deep-copy';
import { TotalCompletesCalculator } from '../../../create-surveys/shared/ui/multicountry/domain/total-completes-calculator';
import { CommonService } from '../../services/common-service/common-service';

@Component({
  selector: 'ps-ui-edit-country-quota-modal',
  templateUrl: './edit-country-quota-modal.component.html',
  styleUrls: ['./edit-country-quota-modal.component.css'],
})
export class EditCountryQuotaModalComponent implements OnInit {
  public multiCountrySurveyData: MultiCountrySpvSurvey[] = [];
  public form!: FormGroup;
  public totalCompletes: number;
  public shouldShowEntrants: boolean = true;

  constructor(
    @Inject(ModalData)
    private _modalData: EditCountryQuotaModalComponent,
    private _modalRef: ModalRef<
      EditCountryQuotaModalComponent,
      { msg?: string; multiCountrySurveyData?: McSpvEachSurveyRequest[] }
    >,
    private _fb: FormBuilder,
    private _toaster: ToasterService,
    private _commonService: CommonService
  ) {
    this.multiCountrySurveyData = deepCopy(
      this._modalData.multiCountrySurveyData
    );
  }

  ngOnInit(): void {
    this._initForm();
    this.totalCompletes = new TotalCompletesCalculator(
      this.multiCountrySurveyData
    ).calculate();
    this._patchFormData(this.multiCountrySurveyData);
    this._decideIfEnterantsColumnShouldShow();
  }

  get multiCountryFormData() {
    return this.form.get('multiCountryFormData') as FormArray;
  }

  private _initForm(): void {
    this.form = this._fb.group({
      multiCountryFormData: this._fb.array(
        (this.multiCountrySurveyData ?? []).map(() =>
          this._fb.group({
            completes_required: [0],
            completes_percentage: [0],
            estimated_clicks: [0],
          })
        )
      ),
    });
  }

  private _patchFormData(
    data: {
      completes_required?: number;
      completes_percentage?: number;
    }[]
  ): void {
    data.forEach((survey, i) => {
      this._patchDataOfSingleItem(i, survey);
    });
  }

  private _patchDataOfSingleItem(
    index: number,
    data: { completes_required?: number; completes_percentage?: number }
  ): void {
    this.multiCountryFormData.at(index).patchValue({
      completes_required: data.completes_required
        ? data.completes_required
        : this._calculateComplets(
            data.completes_percentage!,
            this.totalCompletes
          ),
      completes_percentage: data.completes_percentage
        ? data.completes_percentage
        : this._calculatePercentage(
            data.completes_required!,
            this.totalCompletes
          ),
    });
    this.multiCountrySurveyData[index].estimated_clicks =
      this._getEstimatedClicks(data, index);
  }

  private _calculateComplets(percentage: number, total: number) {
    return !!percentage ? Math.round((total * percentage) / 100) : 0;
  }

  private _calculatePercentage(value: number, total: number) {
    return !!value ? Math.round((value * 100) / Number(total)) : 0;
  }

  public onTotalCompletesChange(value: number) {
    if (this._isInvalidCompletes(Number(value))) {
      return this._toaster.warning(
        notifyMessage.warningMessage.MC_SINGLE_VIEW.LOCALE_QUOTA_MODAL
          .INVALID_INPUT
      );
    }

    this.totalCompletes = Number(value);
  }

  public onCompletesChange(event: Event, index: number) {
    let completes = Number((<HTMLInputElement>event.target).value);

    if (this._isInvalidCompletes(completes)) {
      return this._toaster.warning(
        notifyMessage.warningMessage.MC_SINGLE_VIEW.LOCALE_QUOTA_MODAL
          .INVALID_INPUT
      );
    }

    this.totalCompletes = new TotalCompletesCalculator(
      this.multiCountryFormData.value
    ).calculate();
    this._reAllocatePercentageToEachItem();
  }

  private _reAllocatePercentageToEachItem() {
    this._patchFormData(
      this.multiCountryFormData.value.map(
        ({ completes_required }: { completes_required: number }) => ({
          completes_required,
        })
      )
    );
  }

  public onPercentageChange(event: Event, index: number) {
    let percentage = Number((<HTMLInputElement>event.target).value);

    if (this._isInvalidPercentage(percentage)) {
      return this._toaster.warning(
        notifyMessage.warningMessage.MC_SINGLE_VIEW.LOCALE_QUOTA_MODAL
          .INVALID_INPUT
      );
    }

    this._patchDataOfSingleItem(index, { completes_percentage: percentage });
  }

  private _isInvalidCompletes(completes: number): boolean {
    return !isNaN(completes) && completes < 0;
  }

  private _isInvalidPercentage(percentage: number): boolean {
    return percentage < 0 || percentage > 100;
  }

  private _hasValidTotal() {
    const calculatedTotal = new TotalCompletesCalculator(
      this.multiCountryFormData.value
    ).calculate();
    return calculatedTotal === Number(this.totalCompletes);
  }

  private _hasNonEmptyAllocation() {
    return this.multiCountryFormData.value.every(
      (value: { completes_required: number; completes_percentage: number }) =>
        value.completes_required
    );
  }

  public saveAllocation() {
    const hasEmptyAllocation = !this._hasNonEmptyAllocation();
    const hasInvalidTotal = !this._hasValidTotal();

    if (hasEmptyAllocation) {
      this._toaster.error(
        notifyMessage.errorMessage.COMPLETES_REQUIRED.CANNOT_BE_ZERO
      );
      return false;
    }

    if (hasInvalidTotal) {
      this._toaster.warning(
        notifyMessage.warningMessage.MC_SINGLE_VIEW.LOCALE_QUOTA_MODAL
          .TOTAL_MISMATCH
      );
      return false;
    }
    return this._modalRef.close(<EditLocaleQuotaModalCloseResponse>{
      msg: 'ok',
      totalCompletes: this.totalCompletes,
      multiCountrySurveyData: this.multiCountrySurveyData.map((s, i) => ({
        survey_localization: s.survey_localization,
        completes_required:
          this.multiCountryFormData?.value[i]?.completes_required || 0,
        estimated_clicks: this.multiCountrySurveyData[i]?.estimated_clicks || 0,
      })),
    });
  }

  public distributeQuotaEqually() {
    const data = new CompletesDistributor(
      this.multiCountrySurveyData,
      this.totalCompletes,
      false,
      true
    ).distribute();
    this._patchFormData([...data]);
  }

  public clearDistribution() {
    this.multiCountrySurveyData.forEach((value, i: number) => {
      this._patchDataOfSingleItem(i, {
        completes_percentage: 0,
        completes_required: 0,
      });
    });
  }

  private _getEstimatedClicks(
    data: { completes_required?: number; completes_percentage?: number },
    index: number
  ) {
    if (!data.completes_required) {
      return 0;
    }
    let completesRequired = data.completes_required;
    if (data.completes_percentage) {
      completesRequired = this._calculatePercentage(
        data.completes_required,
        this.totalCompletes
      );
    }
    return this._commonService.calculateClicks(
      completesRequired,
      this.multiCountrySurveyData[index].expected_ir
    );
  }

  private _decideIfEnterantsColumnShouldShow() {
    this.shouldShowEntrants = this.multiCountrySurveyData.some(
      (survey) => survey.click_balancing === 1
    );
  }

  public close(msg?: string) {
    this._modalRef.close(<EditLocaleQuotaModalCloseResponse>{ msg });
  }
}

export type EditLocaleQuotaModalCloseResponse =
  | {
      msg: 'ok';
      multiCountrySurveyData: MultiCountrySpvSurvey[];
      totalCompletes: number;
    }
  | {
      msg?: 'cancel';
    };
